// ============================================================================
// SS3 CHEMISTRY — COMPLETE LEARNING MODULE (11 TOPICS)
// NERDC Scheme of Work — First Term to Third Term (no new-content topics in Third Term)
// ============================================================================

export const WAEC_CHEMISTRY_LEARN_SS3 = [
  {
    "topic": "Food Chemistry",
    "topicCode": "SS3-CHM-01",
    "module": "Chemistry and Society",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">Food supplies the energy and materials our bodies need. The three major organic food classes are <span class=\"learn-keyword\">carbohydrates</span>, <span class=\"learn-keyword\">proteins</span> and <span class=\"learn-keyword\">fats and oils</span>. Each has characteristic structures, tests and reactions.</div><h3 class=\"learn-subheading\">1. Carbohydrates</h3><p class=\"learn-p\">Carbohydrates contain carbon, hydrogen and oxygen, usually in the ratio C<sub>x</sub>(H₂O)<sub>y</sub>. They are polyhydroxy aldehydes or ketones and their derivatives.</p><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Class</th><th>Examples</th><th>Notes</th></tr></thead><tbody><tr><td>Monosaccharides</td><td>Glucose, fructose, galactose (C₆H₁₂O₆)</td><td>Simplest sugars; cannot be hydrolysed</td></tr><tr><td>Disaccharides</td><td>Sucrose, maltose, lactose (C₁₂H₂₂O₁₁)</td><td>Two monosaccharide units joined; hydrolysed to monosaccharides</td></tr><tr><td>Polysaccharides</td><td>Starch, cellulose, glycogen</td><td>Many monosaccharide units; (C₆H₁₀O₅)<sub>n</sub></td></tr></tbody></table></div><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Test</th><th>Observation / meaning</th></tr></thead><tbody><tr><td>Benedict's or Fehling's solution, warm</td><td>Brick-red precipitate of copper(I) oxide = reducing sugar (glucose, fructose, maltose, lactose); sucrose is non-reducing</td></tr><tr><td>Iodine solution</td><td>Blue-black colour = starch</td></tr><tr><td>Molisch's test</td><td>Purple ring at the junction = any carbohydrate</td></tr></tbody></table></div><p class=\"learn-p\">Hydrolysis: sucrose + water → glucose + fructose (with dilute acid or the enzyme invertase). Starch is hydrolysed to glucose. Fermentation of glucose by zymase in yeast gives ethanol and carbon dioxide: C₆H₁₂O₆ → 2C₂H₅OH + 2CO₂.</p><h3 class=\"learn-subheading\">2. Proteins</h3><p class=\"learn-p\">Proteins are polymers of <strong>amino acids</strong> joined by <strong>peptide (amide) bonds</strong>. They contain C, H, O, N and sometimes S. Amino acids contain both an amino group (–NH₂) and a carboxyl group (–COOH).</p><ul style=\"margin-left:20px;\"><li><strong>Biuret test:</strong> NaOH then dilute CuSO₄ gives a violet/purple colour = protein (peptide bonds).</li><li><strong>Ninhydrin test:</strong> purple colour with amino acids.</li><li><strong>Denaturation:</strong> heat, strong acids, alkalis or heavy metals destroy the protein's shape (e.g. cooking an egg).</li><li><strong>Hydrolysis:</strong> boiling with acid or enzymes breaks proteins into amino acids.</li></ul><h3 class=\"learn-subheading\">3. Fats and Oils</h3><p class=\"learn-p\">Fats and oils are <strong>esters of glycerol (propane-1,2,3-triol) and long-chain fatty acids</strong> (triglycerides). Fats are solid at room temperature (mostly saturated); oils are liquid (mostly unsaturated).</p><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Reaction</th><th>Description</th></tr></thead><tbody><tr><td>Hydrolysis</td><td>Fat + water → glycerol + fatty acids</td></tr><tr><td>Saponification</td><td>Fat + NaOH (boiled) → glycerol + soap (sodium salt of fatty acid)</td></tr><tr><td>Hydrogenation</td><td>Vegetable oil + H₂ (nickel catalyst) → margarine (hardened fat)</td></tr><tr><td>Test for unsaturation</td><td>Decolourises bromine water</td></tr><tr><td>Grease-spot test</td><td>Translucent stain on paper = fat or oil</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Importance of Food Classes</h3><ul style=\"margin-left:20px;\"><li>Carbohydrates: main source of energy (about 17 kJ/g).</li><li>Proteins: growth, repair of tissues, enzymes and hormones.</li><li>Fats and oils: concentrated energy (about 39 kJ/g), insulation, carriers of vitamins A, D, E and K.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Sucrose does not reduce Benedict's solution until it is first hydrolysed with dilute acid.</div>",
    "questions": [
      {
        "q": "Which of the following is a monosaccharide?",
        "o": [
          "Sucrose",
          "Maltose",
          "Glucose",
          "Starch"
        ],
        "a": 2,
        "e": "Glucose is a simple sugar (C₆H₁₂O₆) that cannot be hydrolysed further.",
        "h": "It is the sugar in blood.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a disaccharide?",
        "o": [
          "Sucrose",
          "Cellulose",
          "Glucose",
          "Fructose"
        ],
        "a": 0,
        "e": "Sucrose (C₁₂H₂₂O₁₁) is made of glucose and fructose joined together.",
        "h": "Table sugar.",
        "yr": "SS3"
      },
      {
        "q": "Which is a polysaccharide?",
        "o": [
          "Lactose",
          "Starch",
          "Fructose",
          "Glucose"
        ],
        "a": 1,
        "e": "Starch is a polymer of glucose units.",
        "h": "Stored in plants.",
        "yr": "SS3"
      },
      {
        "q": "Starch gives which colour with iodine solution?",
        "o": [
          "Blue-black",
          "Violet",
          "Yellow",
          "Brick-red"
        ],
        "a": 0,
        "e": "Iodine forms a blue-black complex with starch.",
        "h": "Test for starch.",
        "yr": "SS3"
      },
      {
        "q": "Benedict's solution gives a brick-red precipitate with:",
        "o": [
          "sucrose",
          "cellulose",
          "starch",
          "glucose"
        ],
        "a": 3,
        "e": "Glucose is a reducing sugar and reduces Cu²⁺ to red copper(I) oxide.",
        "h": "Reducing sugar.",
        "yr": "SS3"
      },
      {
        "q": "Which sugar is non-reducing?",
        "o": [
          "Fructose",
          "Maltose",
          "Sucrose",
          "Glucose"
        ],
        "a": 2,
        "e": "Sucrose does not reduce Benedict's or Fehling's solution until hydrolysed.",
        "h": "Table sugar.",
        "yr": "SS3"
      },
      {
        "q": "Hydrolysis of sucrose gives:",
        "o": [
          "starch and water",
          "glucose and fructose",
          "glucose only",
          "two molecules of glucose"
        ],
        "a": 1,
        "e": "Sucrose + water → glucose + fructose.",
        "h": "Two different monosaccharides.",
        "yr": "SS3"
      },
      {
        "q": "The general formula of carbohydrates is best written as:",
        "o": [
          "CxHy",
          "Cx(H₂O)y",
          "CxHyN",
          "CnH2n+2"
        ],
        "a": 1,
        "e": "Carbohydrates contain carbon and hydrogen and oxygen in the ratio of water.",
        "h": "'Hydrate of carbon'.",
        "yr": "SS3"
      },
      {
        "q": "Fermentation of glucose by yeast produces:",
        "o": [
          "ethanol and carbon dioxide",
          "ethanoic acid and water",
          "methane and water",
          "glycerol and soap"
        ],
        "a": 0,
        "e": "C₆H₁₂O₆ → 2C₂H₅OH + 2CO₂ (zymase).",
        "h": "Brewing.",
        "yr": "SS3"
      },
      {
        "q": "Proteins are polymers of:",
        "o": [
          "monosaccharides",
          "glycerol",
          "fatty acids",
          "amino acids"
        ],
        "a": 3,
        "e": "Amino acids are joined by peptide bonds.",
        "h": "Building blocks.",
        "yr": "SS3"
      },
      {
        "q": "The linkage joining amino acids is the:",
        "o": [
          "peptide bond",
          "ester bond",
          "glycosidic bond",
          "ionic bond"
        ],
        "a": 0,
        "e": "It is an amide link, –CO–NH–.",
        "h": "Named after 'peptide'.",
        "yr": "SS3"
      },
      {
        "q": "The biuret test is used to detect:",
        "o": [
          "reducing sugars",
          "proteins",
          "fats",
          "starch"
        ],
        "a": 1,
        "e": "A violet colour with NaOH and dilute CuSO₄ shows peptide bonds.",
        "h": "Violet.",
        "yr": "SS3"
      },
      {
        "q": "Which factor can denature a protein?",
        "o": [
          "Cooling to 20 °C",
          "Heat",
          "Adding sugar",
          "Dilute water"
        ],
        "a": 1,
        "e": "Heat changes the protein's shape, as when an egg is boiled.",
        "h": "Cooking.",
        "yr": "SS3"
      },
      {
        "q": "Fats and oils are esters of:",
        "o": [
          "ethanol and starch",
          "glucose and fatty acids",
          "glycerol and fatty acids",
          "glycerol and amino acids"
        ],
        "a": 2,
        "e": "They are triglycerides.",
        "h": "Propane-1,2,3-triol.",
        "yr": "SS3"
      },
      {
        "q": "Boiling fat with sodium hydroxide gives soap and glycerol. This reaction is:",
        "o": [
          "esterification",
          "saponification",
          "hydrogenation",
          "fermentation"
        ],
        "a": 1,
        "e": "Alkaline hydrolysis of fats is saponification.",
        "h": "Soap-making.",
        "yr": "SS3"
      },
      {
        "q": "Vegetable oil is converted to margarine by:",
        "o": [
          "hydrogenation with a nickel catalyst",
          "oxidation",
          "distillation",
          "fermentation"
        ],
        "a": 0,
        "e": "Hydrogen adds across double bonds, hardening the oil.",
        "h": "Add H₂.",
        "yr": "SS3"
      },
      {
        "q": "Unsaturated fats decolourise:",
        "o": [
          "bromine water",
          "litmus",
          "iodine solution",
          "limewater"
        ],
        "a": 0,
        "e": "Bromine adds across the C=C bonds.",
        "h": "Orange-brown to colourless.",
        "yr": "SS3"
      },
      {
        "q": "The greasy translucent spot on paper is a test for:",
        "o": [
          "glucose",
          "fats and oils",
          "starch",
          "proteins"
        ],
        "a": 1,
        "e": "Fats leave a translucent stain.",
        "h": "Grease-spot test.",
        "yr": "SS3"
      },
      {
        "q": "Which food class supplies the most energy per gram?",
        "o": [
          "Vitamins",
          "Carbohydrates",
          "Proteins",
          "Fats"
        ],
        "a": 3,
        "e": "Fats supply about 39 kJ/g compared with about 17 kJ/g for carbohydrates and proteins.",
        "h": "Concentrated energy.",
        "yr": "SS3"
      },
      {
        "q": "The main function of proteins in the body is:",
        "o": [
          "growth and repair of tissues",
          "dissolving vitamins",
          "insulating the nerves only",
          "source of quick energy only"
        ],
        "a": 0,
        "e": "Proteins also form enzymes and hormones.",
        "h": "Body building.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Drugs and Their Effects",
    "topicCode": "SS3-CHM-02",
    "module": "Chemistry and Society",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">A <span class=\"learn-keyword\">drug</span> is any chemical substance that alters the functioning of the body or mind. Drugs used to prevent, treat or cure disease are called <span class=\"learn-keyword\">medicines</span>. Misuse of drugs is called <span class=\"learn-keyword\">drug abuse</span>.</div><h3 class=\"learn-subheading\">1. Classification of Drugs</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Class</th><th>Function</th><th>Examples</th></tr></thead><tbody><tr><td>Analgesics</td><td>Relieve pain</td><td>Aspirin, paracetamol, ibuprofen, codeine</td></tr><tr><td>Antibiotics</td><td>Kill or stop bacteria</td><td>Penicillin, amoxicillin, streptomycin</td></tr><tr><td>Antimalarials</td><td>Treat malaria</td><td>Chloroquine, quinine, artemisinin-based combination therapy (ACT)</td></tr><tr><td>Antacids</td><td>Neutralise excess stomach acid</td><td>Magnesium hydroxide (milk of magnesia), aluminium hydroxide, sodium hydrogencarbonate</td></tr><tr><td>Antiseptics and disinfectants</td><td>Kill germs on skin (antiseptic) or on objects (disinfectant)</td><td>Dettol, iodine, hydrogen peroxide</td></tr><tr><td>Stimulants</td><td>Increase alertness and activity</td><td>Caffeine, nicotine, amphetamines, cocaine</td></tr><tr><td>Depressants (sedatives)</td><td>Slow the nervous system</td><td>Alcohol, barbiturates, diazepam</td></tr><tr><td>Narcotics</td><td>Relieve severe pain; highly addictive</td><td>Morphine, heroin, codeine</td></tr><tr><td>Hallucinogens</td><td>Distort perception</td><td>LSD, cannabis (marijuana)</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Medicinal Uses</h3><ul style=\"margin-left:20px;\"><li>Aspirin is also an anti-inflammatory and reduces fever.</li><li>Antibiotics do not work against viruses.</li><li>Vaccines stimulate immunity; anaesthetics remove sensation during surgery.</li></ul><h3 class=\"learn-subheading\">3. Harmful Chemicals and Drug Abuse</h3><p class=\"learn-p\">Drug abuse is the use of drugs for non-medical reasons or in excess. Effects include addiction (dependence), damage to the liver, kidneys, heart and brain, mental disorders, aggression, poor school and work performance, crime, and death from overdose. Sharing needles spreads HIV and hepatitis.</p><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Substance</th><th>Harmful effect</th></tr></thead><tbody><tr><td>Nicotine (tobacco)</td><td>Addiction, lung cancer, heart disease</td></tr><tr><td>Alcohol</td><td>Liver cirrhosis, impaired judgement, accidents</td></tr><tr><td>Cocaine, heroin</td><td>Severe addiction, overdose, mental damage</td></tr><tr><td>Cannabis</td><td>Hallucination, mental illness, memory loss</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Control</h3><p class=\"learn-p\">In Nigeria the <strong>National Drug Law Enforcement Agency (NDLEA)</strong> fights illicit drugs and NAFDAC regulates drugs and food. Drugs should be taken only as prescribed and never shared.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Summary:</strong> Drugs can heal or harm; the dose, purpose and supervision decide which.</div>",
    "questions": [
      {
        "q": "A drug is best described as:",
        "o": [
          "a chemical that alters the functioning of the body or mind",
          "only a plant product",
          "any food",
          "a poison only"
        ],
        "a": 0,
        "e": "Drugs may treat disease or affect the mind.",
        "h": "Chemical effect.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is an analgesic?",
        "o": [
          "Paracetamol",
          "Caffeine",
          "Chloroquine",
          "Penicillin"
        ],
        "a": 0,
        "e": "Analgesics relieve pain.",
        "h": "Painkiller.",
        "yr": "SS3"
      },
      {
        "q": "Aspirin is used to:",
        "o": [
          "relieve pain and reduce fever and inflammation",
          "treat malaria",
          "neutralise stomach acid",
          "kill bacteria"
        ],
        "a": 0,
        "e": "Aspirin is an analgesic, antipyretic and anti-inflammatory.",
        "h": "Headache.",
        "yr": "SS3"
      },
      {
        "q": "Penicillin is an example of an:",
        "o": [
          "stimulant",
          "analgesic",
          "antacid",
          "antibiotic"
        ],
        "a": 3,
        "e": "It kills bacteria.",
        "h": "Discovered by Fleming.",
        "yr": "SS3"
      },
      {
        "q": "Antibiotics are ineffective against:",
        "o": [
          "bacteria",
          "fungi",
          "all microorganisms",
          "viruses"
        ],
        "a": 3,
        "e": "Antibiotics work on bacteria, not viruses such as those causing colds.",
        "h": "Cold and flu.",
        "yr": "SS3"
      },
      {
        "q": "Chloroquine is used to treat:",
        "o": [
          "cholera",
          "malaria",
          "HIV",
          "typhoid"
        ],
        "a": 1,
        "e": "It is an antimalarial drug.",
        "h": "Mosquito-borne disease.",
        "yr": "SS3"
      },
      {
        "q": "Which is an antacid?",
        "o": [
          "Nicotine",
          "Aspirin",
          "Morphine",
          "Magnesium hydroxide"
        ],
        "a": 3,
        "e": "It neutralises excess stomach acid.",
        "h": "Milk of magnesia.",
        "yr": "SS3"
      },
      {
        "q": "Antacids act by:",
        "o": [
          "killing bacteria",
          "relieving pain in muscles",
          "neutralising excess hydrochloric acid in the stomach",
          "stimulating the brain"
        ],
        "a": 2,
        "e": "They are bases that react with stomach acid.",
        "h": "Acid–base reaction.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a stimulant?",
        "o": [
          "Penicillin",
          "Paracetamol",
          "Morphine",
          "Caffeine"
        ],
        "a": 3,
        "e": "Caffeine increases alertness.",
        "h": "Found in coffee.",
        "yr": "SS3"
      },
      {
        "q": "Alcohol and barbiturates are examples of:",
        "o": [
          "analgesics",
          "stimulants",
          "antibiotics",
          "depressants"
        ],
        "a": 3,
        "e": "They slow down the nervous system.",
        "h": "Slow down.",
        "yr": "SS3"
      },
      {
        "q": "Morphine and heroin are:",
        "o": [
          "antacids",
          "narcotics",
          "vitamins",
          "antiseptics"
        ],
        "a": 1,
        "e": "Narcotics relieve severe pain and are highly addictive.",
        "h": "Opiates.",
        "yr": "SS3"
      },
      {
        "q": "An antiseptic is applied to:",
        "o": [
          "living tissue to kill germs",
          "floors only",
          "food only",
          "clothes only"
        ],
        "a": 0,
        "e": "Disinfectants are used on objects.",
        "h": "Skin.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a hallucinogen?",
        "o": [
          "Chloroquine",
          "Cannabis",
          "Penicillin",
          "Aspirin"
        ],
        "a": 1,
        "e": "Hallucinogens distort perception.",
        "h": "Marijuana.",
        "yr": "SS3"
      },
      {
        "q": "Drug abuse means:",
        "o": [
          "taking vitamins",
          "using vaccines",
          "using drugs for non-medical reasons or in excess",
          "taking drugs as prescribed"
        ],
        "a": 2,
        "e": "Abuse is misuse of drugs.",
        "h": "Misuse.",
        "yr": "SS3"
      },
      {
        "q": "Which is an effect of drug abuse?",
        "o": [
          "Better school performance",
          "Improved health",
          "Longer life",
          "Addiction and damage to organs"
        ],
        "a": 3,
        "e": "Abuse damages the liver, brain and heart.",
        "h": "Harmful.",
        "yr": "SS3"
      },
      {
        "q": "Excessive alcohol consumption can cause damage to the:",
        "o": [
          "teeth only",
          "liver",
          "nails only",
          "hair only"
        ],
        "a": 1,
        "e": "It leads to cirrhosis.",
        "h": "Detoxifying organ.",
        "yr": "SS3"
      },
      {
        "q": "Smoking tobacco is dangerous mainly because of:",
        "o": [
          "water",
          "oxygen",
          "nitrogen",
          "nicotine and tar"
        ],
        "a": 3,
        "e": "Nicotine is addictive; tar causes cancer.",
        "h": "Cigarettes.",
        "yr": "SS3"
      },
      {
        "q": "Sharing needles among drug users can spread:",
        "o": [
          "only headaches",
          "HIV and hepatitis",
          "only colds",
          "malnutrition"
        ],
        "a": 1,
        "e": "Infected blood is transferred.",
        "h": "Blood-borne viruses.",
        "yr": "SS3"
      },
      {
        "q": "In Nigeria, the agency that fights illegal drugs is:",
        "o": [
          "NAFDAC only",
          "NNPC",
          "NDLEA",
          "NERC"
        ],
        "a": 2,
        "e": "National Drug Law Enforcement Agency.",
        "h": "Enforcement.",
        "yr": "SS3"
      },
      {
        "q": "Drugs should be taken:",
        "o": [
          "only as prescribed by a qualified person",
          "mixed with alcohol",
          "shared with friends",
          "in large quantities for quick effect"
        ],
        "a": 0,
        "e": "Correct dose and supervision are essential.",
        "h": "Doctor's advice.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Chemistry in Industry and Everyday Life",
    "topicCode": "SS3-CHM-03",
    "module": "Chemistry and Society",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">Chemistry is applied in almost every part of daily life, from what we eat and wear to how we build homes and clean our surroundings.</div><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Area</th><th>Chemical applications</th></tr></thead><tbody><tr><td>Food</td><td>Preservation (salting, refrigeration, canning, benzoic acid and sodium benzoate); flavours, colours and sweeteners; baking powder (NaHCO₃) makes cakes rise</td></tr><tr><td>Medicine</td><td>Manufacture of drugs, vaccines, antiseptics, anaesthetics, and diagnostic agents such as barium meal for X-rays</td></tr><tr><td>Agriculture</td><td>NPK fertilisers, urea, ammonium nitrate, pesticides, herbicides and lime to correct acidic soils</td></tr><tr><td>Textiles</td><td>Natural fibres (cotton, wool, silk) and synthetic fibres (nylon, polyester); dyes and bleaches</td></tr><tr><td>Construction</td><td>Cement and concrete, glass, steel and other alloys, paints and plastics</td></tr><tr><td>Household products</td><td>Soaps, detergents, bleach (sodium chlorate(I)), disinfectants, toothpaste, cosmetics, matches, insecticides</td></tr></tbody></table></div><h3 class=\"learn-subheading\">Selected Examples</h3><ul style=\"margin-left:20px;\"><li><strong>Fertiliser:</strong> N provides leaf growth, P root growth and K flowering and fruiting.</li><li><strong>Synthetic fibres:</strong> nylon (polyamide) is strong and elastic; polyester (Terylene) resists wrinkling.</li><li><strong>Cement:</strong> made from limestone and clay; sets when mixed with water.</li><li><strong>Glass:</strong> made by fusing sand (SiO₂), limestone and sodium carbonate.</li></ul><h3 class=\"learn-subheading\">Advantages and Risks</h3><p class=\"learn-p\">Chemical products improve living standards but misuse or improper disposal causes pollution and health hazards; safe handling, labelling and waste treatment are essential.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Note:</strong> Balanced use of fertilisers is important; excess runs off into rivers and causes eutrophication.</div>",
    "questions": [
      {
        "q": "Which of these is used as a food preservative?",
        "o": [
          "Bleach",
          "Sodium hydroxide",
          "Sodium benzoate",
          "Kerosene"
        ],
        "a": 2,
        "e": "Benzoates and salting slow spoilage.",
        "h": "Additive.",
        "yr": "SS3"
      },
      {
        "q": "Baking powder contains:",
        "o": [
          "sodium chloride",
          "sodium hydrogencarbonate",
          "sodium hydroxide",
          "calcium oxide"
        ],
        "a": 1,
        "e": "It releases CO₂ that makes dough rise.",
        "h": "NaHCO₃.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a synthetic fibre?",
        "o": [
          "Silk",
          "Nylon",
          "Cotton",
          "Wool"
        ],
        "a": 1,
        "e": "Nylon is a polyamide made from petrochemicals.",
        "h": "Not natural.",
        "yr": "SS3"
      },
      {
        "q": "Cotton is composed mainly of:",
        "o": [
          "polyamide",
          "protein",
          "keratin",
          "cellulose"
        ],
        "a": 3,
        "e": "It is a plant carbohydrate.",
        "h": "From the cotton plant.",
        "yr": "SS3"
      },
      {
        "q": "Wool and silk are natural fibres made of:",
        "o": [
          "glycerol",
          "protein",
          "polyester",
          "cellulose"
        ],
        "a": 1,
        "e": "Animal fibres are proteins.",
        "h": "Animal source.",
        "yr": "SS3"
      },
      {
        "q": "Cement is made mainly from:",
        "o": [
          "coal and sulphur",
          "sand and salt",
          "limestone and clay",
          "glass and soda"
        ],
        "a": 2,
        "e": "They are heated in a kiln.",
        "h": "Calcium carbonate.",
        "yr": "SS3"
      },
      {
        "q": "Glass is made by fusing sand with limestone and:",
        "o": [
          "iron oxide",
          "calcium chloride",
          "sodium chloride",
          "sodium carbonate"
        ],
        "a": 3,
        "e": "Soda-lime glass.",
        "h": "Soda ash.",
        "yr": "SS3"
      },
      {
        "q": "In NPK fertiliser, 'K' stands for:",
        "o": [
          "calcium",
          "phosphorus",
          "nitrogen",
          "potassium"
        ],
        "a": 3,
        "e": "N nitrogen, P phosphorus, K potassium.",
        "h": "From Latin 'kalium'.",
        "yr": "SS3"
      },
      {
        "q": "Nitrogen in fertilisers mainly promotes:",
        "o": [
          "leaf growth",
          "root growth",
          "flower colour",
          "seed dormancy"
        ],
        "a": 0,
        "e": "Nitrogen supports vegetative growth.",
        "h": "Green leaves.",
        "yr": "SS3"
      },
      {
        "q": "Lime (calcium oxide or hydroxide) is added to soil to:",
        "o": [
          "add nitrogen",
          "increase acidity",
          "kill worms",
          "correct acidity"
        ],
        "a": 3,
        "e": "It neutralises acidic soils.",
        "h": "Raise pH.",
        "yr": "SS3"
      },
      {
        "q": "Pesticides are used in agriculture to:",
        "o": [
          "kill pests",
          "produce oxygen",
          "dissolve soil",
          "feed plants"
        ],
        "a": 0,
        "e": "Insecticides, herbicides and fungicides.",
        "h": "Protect crops.",
        "yr": "SS3"
      },
      {
        "q": "Excess fertiliser washing into rivers causes:",
        "o": [
          "acid rain",
          "eutrophication",
          "noise pollution",
          "ozone depletion"
        ],
        "a": 1,
        "e": "Nutrients cause algal blooms.",
        "h": "Water pollution.",
        "yr": "SS3"
      },
      {
        "q": "Household bleach commonly contains:",
        "o": [
          "sodium chlorate(I)",
          "sodium carbonate",
          "calcium sulphate",
          "sodium nitrate"
        ],
        "a": 0,
        "e": "It is a disinfectant and bleaching agent.",
        "h": "NaOCl.",
        "yr": "SS3"
      },
      {
        "q": "Soaps are made from fats or oils and:",
        "o": [
          "sodium hydroxide",
          "ethanol",
          "sulphuric acid",
          "hydrochloric acid"
        ],
        "a": 0,
        "e": "Saponification.",
        "h": "An alkali.",
        "yr": "SS3"
      },
      {
        "q": "Steel is an alloy of iron with:",
        "o": [
          "zinc",
          "tin only",
          "copper",
          "carbon"
        ],
        "a": 3,
        "e": "Carbon strengthens iron.",
        "h": "Non-metal.",
        "yr": "SS3"
      },
      {
        "q": "Barium meal is used in medicine for:",
        "o": [
          "treating malaria",
          "relieving pain",
          "killing bacteria",
          "X-ray imaging of the digestive system"
        ],
        "a": 3,
        "e": "It is opaque to X-rays.",
        "h": "Diagnosis.",
        "yr": "SS3"
      },
      {
        "q": "Refrigeration preserves food because it:",
        "o": [
          "slows the growth of microorganisms",
          "adds preservatives",
          "kills all enzymes permanently",
          "increases water content"
        ],
        "a": 0,
        "e": "Low temperature reduces microbial activity.",
        "h": "Cold.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a household product made through industrial chemistry?",
        "o": [
          "Rainwater",
          "Detergent",
          "Sand",
          "Granite"
        ],
        "a": 1,
        "e": "Detergents are manufactured from petrochemicals.",
        "h": "Cleaning.",
        "yr": "SS3"
      },
      {
        "q": "Improper disposal of chemical products can cause:",
        "o": [
          "better soil",
          "reduced disease",
          "pollution and health hazards",
          "cleaner water"
        ],
        "a": 2,
        "e": "Safe handling is required.",
        "h": "Harm.",
        "yr": "SS3"
      },
      {
        "q": "Which is an example of a natural fibre?",
        "o": [
          "Nylon",
          "PVC",
          "Terylene",
          "Silk"
        ],
        "a": 3,
        "e": "Silk comes from silkworms.",
        "h": "Animal origin.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Environmental Chemistry",
    "topicCode": "SS3-CHM-04",
    "module": "Environment and Industry",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Pollution</span> is the introduction of harmful substances or energy into the environment at levels that damage living things or natural systems. The substances are called <span class=\"learn-keyword\">pollutants</span>.</div><h3 class=\"learn-subheading\">1. Types of Pollution, Causes and Effects</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Type</th><th>Common pollutants and sources</th><th>Effects</th></tr></thead><tbody><tr><td>Air</td><td>CO from incomplete combustion; SO₂ from burning fossil fuels; NO<sub>x</sub> from vehicles; CO₂; CFCs; smoke and dust; gas flaring</td><td>Respiratory disease; acid rain; global warming; ozone layer depletion</td></tr><tr><td>Water</td><td>Oil spills; sewage; industrial effluents; heavy metals (Hg, Pb); fertiliser run-off; detergents</td><td>Death of aquatic life; waterborne diseases (cholera, typhoid); eutrophication</td></tr><tr><td>Soil</td><td>Pesticides; plastics and non-biodegradable waste; mining; oil spills; acid rain</td><td>Loss of fertility; toxic crops; harm to soil organisms</td></tr><tr><td>Noise</td><td>Generators, machines, traffic, loud music</td><td>Hearing loss, stress, poor sleep</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Key Environmental Problems</h3><ul style=\"margin-left:20px;\"><li><strong>Acid rain:</strong> SO₂ and NO₂ dissolve in rain to form acids (pH below 5.6); damages buildings, crops and aquatic life.</li><li><strong>Greenhouse effect / global warming:</strong> CO₂, CH₄ and other gases trap heat.</li><li><strong>Ozone depletion:</strong> CFCs break down ozone in the stratosphere, letting harmful UV reach Earth.</li><li><strong>Eutrophication:</strong> excess nitrates and phosphates cause algal blooms that use up oxygen in water.</li></ul><h3 class=\"learn-subheading\">3. Methods of Remediation and Control</h3><ul style=\"margin-left:20px;\"><li>Use catalytic converters and unleaded fuel; reduce gas flaring; plant trees.</li><li>Treat sewage and industrial waste before discharge; use booms and dispersants for oil spills.</li><li>Recycle, reuse and reduce waste; use biodegradable materials.</li><li>Use organic manure and integrated pest management.</li><li>Enforce environmental laws (e.g. NESREA) and use noise barriers, mufflers and ear protection.</li><li>Use renewable energy such as solar and wind.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Carbon monoxide is poisonous because it combines with haemoglobin more strongly than oxygen does.</div>",
    "questions": [
      {
        "q": "Pollution is the:",
        "o": [
          "introduction of harmful substances into the environment",
          "cleaning of water",
          "planting of trees",
          "removal of all waste"
        ],
        "a": 0,
        "e": "It damages living things and natural systems.",
        "h": "Contamination.",
        "yr": "SS3"
      },
      {
        "q": "Which gas is produced by incomplete combustion of fuel?",
        "o": [
          "Oxygen",
          "Carbon dioxide only",
          "Carbon monoxide",
          "Nitrogen"
        ],
        "a": 2,
        "e": "CO is a poisonous gas.",
        "h": "Poor air supply.",
        "yr": "SS3"
      },
      {
        "q": "Carbon monoxide is poisonous because it:",
        "o": [
          "turns blood into water",
          "corrodes the lungs",
          "combines with haemoglobin more strongly than oxygen",
          "neutralises stomach acid"
        ],
        "a": 2,
        "e": "It prevents oxygen transport.",
        "h": "Blood.",
        "yr": "SS3"
      },
      {
        "q": "Acid rain is caused mainly by:",
        "o": [
          "oxygen and nitrogen",
          "sulphur(IV) oxide and nitrogen oxides",
          "hydrogen and water",
          "argon and helium"
        ],
        "a": 1,
        "e": "They dissolve in rain to form acids.",
        "h": "Industrial gases.",
        "yr": "SS3"
      },
      {
        "q": "Normal rainwater has a pH of about:",
        "o": [
          "5.6",
          "7",
          "1",
          "10"
        ],
        "a": 0,
        "e": "It is slightly acidic due to dissolved CO₂.",
        "h": "Slightly below 7.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a greenhouse gas?",
        "o": [
          "Oxygen",
          "Argon",
          "Nitrogen",
          "Carbon dioxide"
        ],
        "a": 3,
        "e": "CO₂ and methane trap heat.",
        "h": "Global warming.",
        "yr": "SS3"
      },
      {
        "q": "Depletion of the ozone layer is mainly caused by:",
        "o": [
          "chlorofluorocarbons (CFCs)",
          "nitrogen",
          "carbon dioxide",
          "noble gases"
        ],
        "a": 0,
        "e": "CFCs release chlorine atoms that destroy ozone.",
        "h": "Old refrigerants.",
        "yr": "SS3"
      },
      {
        "q": "The ozone layer protects Earth from:",
        "o": [
          "oxygen",
          "rain",
          "harmful ultraviolet radiation",
          "wind"
        ],
        "a": 2,
        "e": "It absorbs UV.",
        "h": "Sun's rays.",
        "yr": "SS3"
      },
      {
        "q": "Eutrophication is caused by excess:",
        "o": [
          "carbon dioxide in air",
          "oil in soil",
          "nitrates and phosphates in water",
          "noise"
        ],
        "a": 2,
        "e": "It leads to algal blooms and oxygen depletion.",
        "h": "Fertiliser run-off.",
        "yr": "SS3"
      },
      {
        "q": "An oil spill mainly causes:",
        "o": [
          "only air pollution",
          "noise pollution",
          "ozone depletion",
          "water pollution"
        ],
        "a": 3,
        "e": "It harms aquatic life.",
        "h": "Niger Delta.",
        "yr": "SS3"
      },
      {
        "q": "Which is a water-borne disease?",
        "o": [
          "Hearing loss",
          "Common cold",
          "Cholera",
          "Asthma"
        ],
        "a": 2,
        "e": "Contaminated water spreads cholera.",
        "h": "Diarrhoeal.",
        "yr": "SS3"
      },
      {
        "q": "Heavy metals like mercury and lead in water are:",
        "o": [
          "nutrients",
          "useful fertilisers",
          "toxic to humans",
          "harmless"
        ],
        "a": 2,
        "e": "They accumulate in the body.",
        "h": "Poison.",
        "yr": "SS3"
      },
      {
        "q": "Noise pollution can cause:",
        "o": [
          "acid rain",
          "ozone depletion",
          "eutrophication",
          "hearing loss and stress"
        ],
        "a": 3,
        "e": "Loud sounds damage hearing.",
        "h": "Generators.",
        "yr": "SS3"
      },
      {
        "q": "Non-biodegradable plastic waste harms the:",
        "o": [
          "only the sun",
          "soil",
          "only the moon",
          "only the stars"
        ],
        "a": 1,
        "e": "It persists and reduces fertility.",
        "h": "Land pollution.",
        "yr": "SS3"
      },
      {
        "q": "A catalytic converter in a car reduces:",
        "o": [
          "emission of harmful gases",
          "fuel cost only",
          "engine noise only",
          "tyre wear"
        ],
        "a": 0,
        "e": "It converts CO and NOx into less harmful gases.",
        "h": "Exhaust.",
        "yr": "SS3"
      },
      {
        "q": "Which is a method of controlling air pollution?",
        "o": [
          "Burning more waste",
          "Planting trees and using cleaner fuels",
          "Using leaded petrol",
          "Gas flaring"
        ],
        "a": 1,
        "e": "Trees absorb CO₂.",
        "h": "Cleaner air.",
        "yr": "SS3"
      },
      {
        "q": "Reducing, reusing and recycling waste is done to:",
        "o": [
          "lower pollution and conserve resources",
          "spread disease",
          "destroy soil",
          "increase waste"
        ],
        "a": 0,
        "e": "The 3Rs.",
        "h": "Waste management.",
        "yr": "SS3"
      },
      {
        "q": "Sewage should be treated before discharge to:",
        "o": [
          "produce noise",
          "prevent water pollution and disease",
          "form acid rain",
          "increase CO₂"
        ],
        "a": 1,
        "e": "Treatment removes pathogens.",
        "h": "Public health.",
        "yr": "SS3"
      },
      {
        "q": "Which agency enforces environmental standards in Nigeria?",
        "o": [
          "NDLEA",
          "CBN",
          "NESREA",
          "JAMB"
        ],
        "a": 2,
        "e": "National Environmental Standards and Regulations Enforcement Agency.",
        "h": "Environment.",
        "yr": "SS3"
      },
      {
        "q": "Gas flaring in oil fields contributes to:",
        "o": [
          "cleaner water",
          "air pollution and global warming",
          "soil enrichment",
          "noise-free areas"
        ],
        "a": 1,
        "e": "It releases CO₂, CH₄ and other gases.",
        "h": "Burning waste gas.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Industrial Chemistry",
    "topicCode": "SS3-CHM-05",
    "module": "Environment and Industry",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">Industrial chemistry is the large-scale manufacture of useful chemical products. Nigeria has many chemical industries built on its natural resources, especially crude oil and limestone.</div><h3 class=\"learn-subheading\">1. Petroleum Industry</h3><p class=\"learn-p\">Crude petroleum is a mixture of hydrocarbons. It is separated in a refinery by <strong>fractional distillation</strong> into fractions with different boiling ranges.</p><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Fraction</th><th>Use</th></tr></thead><tbody><tr><td>Refinery gas (LPG)</td><td>Cooking gas</td></tr><tr><td>Petrol (gasoline)</td><td>Fuel for cars</td></tr><tr><td>Kerosene</td><td>Jet fuel, lamps and stoves</td></tr><tr><td>Diesel (gas oil)</td><td>Fuel for trucks and generators</td></tr><tr><td>Lubricating oil and wax</td><td>Lubricants, candles, polish</td></tr><tr><td>Bitumen (residue)</td><td>Road tarring</td></tr></tbody></table></div><p class=\"learn-p\">Nigeria's refineries include Port Harcourt, Warri and Kaduna, and the privately owned Dangote refinery near Lagos. Petrochemicals from petroleum are the raw materials for plastics, detergents and fertilisers.</p><h3 class=\"learn-subheading\">2. Other Chemical Industries</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Industry</th><th>Raw materials / process</th></tr></thead><tbody><tr><td>Cement</td><td>Limestone (CaCO₃) and clay heated in a rotary kiln to form clinker; ground with a little gypsum. Locations: Ewekoro, Obajana, Sagamu, Nkalagu</td></tr><tr><td>Soap</td><td>Fats or oils boiled with sodium hydroxide (saponification); salt added to precipitate the soap</td></tr><tr><td>Paint</td><td>Pigment (colour), binder or resin, solvent (thinner) and additives</td></tr><tr><td>Textiles</td><td>Spinning fibres into yarn, weaving or knitting, then dyeing and finishing</td></tr><tr><td>Beverages and brewing</td><td>Fermentation of sugars by yeast produces alcohol; soft drinks contain water, sugar, flavour and dissolved CO₂</td></tr><tr><td>Fertiliser</td><td>Ammonia from the Haber process is converted to urea and ammonium salts</td></tr></tbody></table></div><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Summary:</strong> Industrial location depends on raw materials, power, water, labour and market.</div>",
    "questions": [
      {
        "q": "Crude petroleum is separated into fractions by:",
        "o": [
          "filtration",
          "crystallisation",
          "fractional distillation",
          "electrolysis"
        ],
        "a": 2,
        "e": "Fractions have different boiling ranges.",
        "h": "Boiling points.",
        "yr": "SS3"
      },
      {
        "q": "Which fraction is used for tarring roads?",
        "o": [
          "Diesel",
          "Kerosene",
          "Petrol",
          "Bitumen"
        ],
        "a": 3,
        "e": "Bitumen is the residue.",
        "h": "Heavy residue.",
        "yr": "SS3"
      },
      {
        "q": "LPG is mainly used as:",
        "o": [
          "road surface",
          "lubricant",
          "cooking gas",
          "jet fuel"
        ],
        "a": 2,
        "e": "Liquefied petroleum gas.",
        "h": "Kitchen.",
        "yr": "SS3"
      },
      {
        "q": "Which fraction fuels aircraft?",
        "o": [
          "Bitumen",
          "Wax",
          "Refinery gas",
          "Kerosene"
        ],
        "a": 3,
        "e": "Jet fuel is a kerosene fraction.",
        "h": "Aviation.",
        "yr": "SS3"
      },
      {
        "q": "Diesel is used mainly in:",
        "o": [
          "road tarring",
          "lubrication of watches",
          "candles",
          "trucks and generators"
        ],
        "a": 3,
        "e": "Gas oil fraction.",
        "h": "Heavy engines.",
        "yr": "SS3"
      },
      {
        "q": "Which Nigerian city has an old refinery?",
        "o": [
          "Enugu",
          "Port Harcourt",
          "Ilorin",
          "Kano"
        ],
        "a": 1,
        "e": "Others are Warri and Kaduna.",
        "h": "Niger Delta.",
        "yr": "SS3"
      },
      {
        "q": "Cement is made by heating limestone and:",
        "o": [
          "sand only",
          "coal only",
          "clay",
          "salt"
        ],
        "a": 2,
        "e": "In a rotary kiln.",
        "h": "Aluminosilicate.",
        "yr": "SS3"
      },
      {
        "q": "The product of the kiln in cement making is called:",
        "o": [
          "brine",
          "clinker",
          "bitumen",
          "slag"
        ],
        "a": 1,
        "e": "It is ground with gypsum.",
        "h": "Lumps.",
        "yr": "SS3"
      },
      {
        "q": "Gypsum is added to cement to:",
        "o": [
          "give colour",
          "increase weight",
          "dissolve clay",
          "control the setting time"
        ],
        "a": 3,
        "e": "It slows the setting.",
        "h": "Retarder.",
        "yr": "SS3"
      },
      {
        "q": "Soap is manufactured by:",
        "o": [
          "neutralisation of acids only",
          "fractional distillation",
          "electrolysis of brine",
          "saponification of fats or oils with alkali"
        ],
        "a": 3,
        "e": "Common salt is added to separate the soap.",
        "h": "NaOH.",
        "yr": "SS3"
      },
      {
        "q": "Adding salt during soap making:",
        "o": [
          "dissolves the soap",
          "removes glycerol",
          "adds colour",
          "precipitates (salts out) the soap"
        ],
        "a": 3,
        "e": "Soap is less soluble in brine.",
        "h": "Separation.",
        "yr": "SS3"
      },
      {
        "q": "Paint consists of pigment, binder and:",
        "o": [
          "cement",
          "soap",
          "sugar",
          "solvent"
        ],
        "a": 3,
        "e": "The thinner evaporates on drying.",
        "h": "Liquid part.",
        "yr": "SS3"
      },
      {
        "q": "The role of the pigment in paint is to:",
        "o": [
          "bind the paint",
          "give colour and cover",
          "thin the paint",
          "kill germs"
        ],
        "a": 1,
        "e": "Pigment provides colour.",
        "h": "Colour.",
        "yr": "SS3"
      },
      {
        "q": "Fermentation in brewing is carried out by:",
        "o": [
          "viruses",
          "bacteria only",
          "yeast",
          "fungi spores in soil only"
        ],
        "a": 2,
        "e": "Yeast converts sugars to ethanol.",
        "h": "Zymase.",
        "yr": "SS3"
      },
      {
        "q": "Soft drinks contain dissolved:",
        "o": [
          "oxygen",
          "hydrogen",
          "carbon dioxide",
          "nitrogen"
        ],
        "a": 2,
        "e": "It gives fizz.",
        "h": "Carbonated.",
        "yr": "SS3"
      },
      {
        "q": "The fertiliser industry uses ammonia from the:",
        "o": [
          "Haber process",
          "Solvay process",
          "Contact process",
          "Ostwald process only"
        ],
        "a": 0,
        "e": "N₂ + 3H₂ ⇌ 2NH₃.",
        "h": "Iron catalyst.",
        "yr": "SS3"
      },
      {
        "q": "Which factor is important in locating a chemical industry?",
        "o": [
          "Availability of raw materials",
          "Absence of power",
          "Distance from all customers",
          "Lack of water"
        ],
        "a": 0,
        "e": "Also power, water, labour and market.",
        "h": "Inputs.",
        "yr": "SS3"
      },
      {
        "q": "The main raw materials for cement are found in:",
        "o": [
          "deserts only",
          "glaciers",
          "the ocean floor only",
          "limestone-bearing areas"
        ],
        "a": 3,
        "e": "Ewekoro and Obajana have limestone.",
        "h": "Rock source.",
        "yr": "SS3"
      },
      {
        "q": "Petrochemicals are raw materials for:",
        "o": [
          "only food",
          "only wood",
          "plastics and detergents",
          "only cement"
        ],
        "a": 2,
        "e": "Derived from petroleum.",
        "h": "Synthetic products.",
        "yr": "SS3"
      },
      {
        "q": "Textile manufacture involves spinning, weaving and:",
        "o": [
          "smelting",
          "fermentation",
          "dyeing",
          "distillation"
        ],
        "a": 2,
        "e": "Then finishing.",
        "h": "Colour.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Chemical Calculations Practice",
    "topicCode": "SS3-CHM-06",
    "module": "Quantitative Chemistry",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">Quantitative chemistry links the masses, volumes and concentrations of substances through the <span class=\"learn-keyword\">mole</span>. One mole of any substance contains the Avogadro number of particles, <strong>6.02 × 10²³</strong>.</div><h3 class=\"learn-subheading\">1. Key Formulae</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Quantity</th><th>Formula</th></tr></thead><tbody><tr><td>Number of moles (from mass)</td><td>n = mass ÷ molar mass</td></tr><tr><td>Number of moles (from particles)</td><td>n = number of particles ÷ 6.02 × 10²³</td></tr><tr><td>Molar volume of a gas at s.t.p.</td><td>22.4 dm³ per mole (s.t.p. = 0 °C and 101 325 Pa)</td></tr><tr><td>Concentration</td><td>c = n ÷ V (mol/dm³); mass concentration = c × molar mass (g/dm³)</td></tr><tr><td>Percentage composition</td><td>(mass of element ÷ molar mass of compound) × 100</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Titration Calculations</h3><p class=\"learn-p\">For the reaction aA + bB → products, with acid A and base B:  <strong>(C<sub>A</sub>V<sub>A</sub>) ÷ (C<sub>B</sub>V<sub>B</sub>) = n<sub>A</sub> ÷ n<sub>B</sub></strong>, where n is the coefficient from the balanced equation.</p><p class=\"learn-p\"><em>Example:</em> 25.0 cm³ of NaOH is neutralised by 20.0 cm³ of 0.10 mol/dm³ HCl. HCl + NaOH → NaCl + H₂O (1:1). C<sub>B</sub> = (0.10 × 20.0) ÷ 25.0 = <strong>0.080 mol/dm³</strong>.</p><h3 class=\"learn-subheading\">3. Empirical and Molecular Formulae</h3><p class=\"learn-p\">The <strong>empirical formula</strong> is the simplest whole-number ratio of atoms. To find it: divide each percentage by the atomic mass, divide by the smallest result, and round to whole numbers. The <strong>molecular formula</strong> = (empirical formula)<sub>n</sub>, where n = molar mass ÷ empirical formula mass.</p><p class=\"learn-p\"><em>Example:</em> A compound is 40% C, 6.7% H and 53.3% O (H = 1, C = 12, O = 16). Moles: C 3.33, H 6.7, O 3.33. Ratio 1 : 2 : 1, so the empirical formula is <strong>CH₂O</strong>. If the molar mass is 180, n = 180 ÷ 30 = 6, so the molecular formula is <strong>C₆H₁₂O₆</strong>.</p><h3 class=\"learn-subheading\">4. Stoichiometry</h3><p class=\"learn-p\"><em>Example:</em> 2H₂ + O₂ → 2H₂O. 4 g of hydrogen (2 mol) produces 2 mol of water = 36 g. Always balance the equation first and use mole ratios.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Convert everything to moles first; the mole ratio in the balanced equation does the rest.</div>",
    "questions": [
      {
        "q": "The Avogadro number is:",
        "o": [
          "6.02 × 10²³",
          "6.02 × 10²²",
          "3.0 × 10⁸",
          "22.4"
        ],
        "a": 0,
        "e": "It is the number of particles in one mole.",
        "h": "Constant.",
        "yr": "SS3"
      },
      {
        "q": "The molar volume of a gas at s.t.p. is:",
        "o": [
          "24 dm³",
          "22.4 dm³",
          "1 dm³",
          "2.24 dm³"
        ],
        "a": 1,
        "e": "One mole of any gas occupies 22.4 dm³ at s.t.p.",
        "h": "Standard temperature and pressure.",
        "yr": "SS3"
      },
      {
        "q": "The number of moles in 18 g of water (H = 1, O = 16) is:",
        "o": [
          "0.5",
          "1",
          "2",
          "18"
        ],
        "a": 1,
        "e": "M(H₂O) = 18; n = 18 ÷ 18 = 1.",
        "h": "n = m/M.",
        "yr": "SS3"
      },
      {
        "q": "The mass of 0.5 mol of NaCl (Na = 23, Cl = 35.5) is:",
        "o": [
          "29.25 g",
          "117 g",
          "58.5 g",
          "11.7 g"
        ],
        "a": 0,
        "e": "0.5 × 58.5 = 29.25 g.",
        "h": "m = n × M.",
        "yr": "SS3"
      },
      {
        "q": "The volume at s.t.p. of 0.25 mol of a gas is:",
        "o": [
          "2.24 dm³",
          "11.2 dm³",
          "5.6 dm³",
          "22.4 dm³"
        ],
        "a": 2,
        "e": "0.25 × 22.4 = 5.6 dm³.",
        "h": "V = n × 22.4.",
        "yr": "SS3"
      },
      {
        "q": "The molar mass of CaCO₃ (Ca = 40, C = 12, O = 16) is:",
        "o": [
          "100 g/mol",
          "56 g/mol",
          "84 g/mol",
          "68 g/mol"
        ],
        "a": 0,
        "e": "40 + 12 + 48 = 100.",
        "h": "Add the atomic masses.",
        "yr": "SS3"
      },
      {
        "q": "The number of molecules in 2 mol of a substance is:",
        "o": [
          "3.01 × 10²³",
          "6.02 × 10²³",
          "1.204 × 10²⁴",
          "1.204 × 10²³"
        ],
        "a": 2,
        "e": "2 × 6.02 × 10²³.",
        "h": "Multiply by N_A.",
        "yr": "SS3"
      },
      {
        "q": "The concentration of a solution containing 4 g of NaOH (Na = 23, O = 16, H = 1) in 500 cm³ is:",
        "o": [
          "2.0 mol/dm³",
          "0.40 mol/dm³",
          "0.20 mol/dm³",
          "0.10 mol/dm³"
        ],
        "a": 2,
        "e": "n = 4 ÷ 40 = 0.1 mol; c = 0.1 ÷ 0.5 = 0.2.",
        "h": "Convert cm³ to dm³.",
        "yr": "SS3"
      },
      {
        "q": "25.0 cm³ of NaOH is neutralised by 20.0 cm³ of 0.10 mol/dm³ HCl. The concentration of NaOH is:",
        "o": [
          "0.125 mol/dm³",
          "0.10 mol/dm³",
          "0.080 mol/dm³",
          "0.20 mol/dm³"
        ],
        "a": 2,
        "e": "C = (0.10 × 20) ÷ 25 = 0.080.",
        "h": "1:1 reaction.",
        "yr": "SS3"
      },
      {
        "q": "In a titration, the volume of acid needed is 20.0 cm³ of 0.10 M H₂SO₄ to neutralise NaOH. Moles of H₂SO₄ used are:",
        "o": [
          "0.020 mol",
          "0.010 mol",
          "0.0020 mol",
          "0.0040 mol"
        ],
        "a": 2,
        "e": "0.10 × 20.0 ÷ 1000 = 0.0020.",
        "h": "n = CV/1000.",
        "yr": "SS3"
      },
      {
        "q": "The empirical formula of a compound with 85.7% carbon and 14.3% hydrogen (C = 12, H = 1) is:",
        "o": [
          "CH₃",
          "C₂H",
          "CH",
          "CH₂"
        ],
        "a": 3,
        "e": "C: 7.14, H: 14.3, ratio 1 : 2.",
        "h": "Divide by atomic masses.",
        "yr": "SS3"
      },
      {
        "q": "If the empirical formula is CH₂ and the molar mass is 42, the molecular formula is:",
        "o": [
          "CH₂",
          "C₄H₈",
          "C₂H₄",
          "C₃H₆"
        ],
        "a": 3,
        "e": "n = 42 ÷ 14 = 3.",
        "h": "n = M ÷ empirical mass.",
        "yr": "SS3"
      },
      {
        "q": "The percentage of nitrogen in NH₄NO₃ (N = 14, H = 1, O = 16) is:",
        "o": [
          "35%",
          "28%",
          "70%",
          "17.5%"
        ],
        "a": 0,
        "e": "M = 80; N = 28; 28 ÷ 80 = 35%.",
        "h": "Two nitrogen atoms.",
        "yr": "SS3"
      },
      {
        "q": "The mass of CO₂ obtained by heating 10 g of CaCO₃ (C = 12, O = 16, Ca = 40) is:",
        "o": [
          "44 g",
          "10 g",
          "5.6 g",
          "4.4 g"
        ],
        "a": 3,
        "e": "10 g = 0.1 mol; CaCO₃ → CaO + CO₂ gives 0.1 mol CO₂ = 4.4 g.",
        "h": "1:1 ratio.",
        "yr": "SS3"
      },
      {
        "q": "The mass concentration of 0.1 mol/dm³ HCl (H = 1, Cl = 35.5) is:",
        "o": [
          "3.65 g/dm³",
          "36.5 g/dm³",
          "0.365 g/dm³",
          "7.3 g/dm³"
        ],
        "a": 0,
        "e": "0.1 × 36.5.",
        "h": "c × M.",
        "yr": "SS3"
      },
      {
        "q": "The number of moles in 11.5 g of sodium (Na = 23) is:",
        "o": [
          "2.0 mol",
          "0.25 mol",
          "1.0 mol",
          "0.5 mol"
        ],
        "a": 3,
        "e": "11.5 ÷ 23 = 0.5.",
        "h": "n = m/M.",
        "yr": "SS3"
      },
      {
        "q": "What volume of hydrogen at s.t.p. is produced when 6.5 g of zinc (Zn = 65) reacts with excess acid?",
        "o": [
          "4.48 dm³",
          "22.4 dm³",
          "2.24 dm³",
          "0.224 dm³"
        ],
        "a": 2,
        "e": "6.5 g = 0.1 mol; 1 mol Zn gives 1 mol H₂; 0.1 × 22.4 = 2.24 dm³.",
        "h": "Zn + 2HCl → ZnCl₂ + H₂.",
        "yr": "SS3"
      },
      {
        "q": "What mass of water is formed when 4 g of hydrogen reacts completely with oxygen (H = 1, O = 16)? 2H₂ + O₂ → 2H₂O",
        "o": [
          "18 g",
          "36 g",
          "72 g",
          "4 g"
        ],
        "a": 1,
        "e": "4 g = 2 mol H₂ gives 2 mol H₂O = 36 g.",
        "h": "2:2 mole ratio.",
        "yr": "SS3"
      },
      {
        "q": "In stoichiometry, the first step in solving a reaction problem is to:",
        "o": [
          "measure temperature only",
          "write and balance the equation",
          "guess the products",
          "find the density only"
        ],
        "a": 1,
        "e": "The mole ratio comes from the balanced equation.",
        "h": "Balance first.",
        "yr": "SS3"
      },
      {
        "q": "s.t.p. stands for:",
        "o": [
          "solid temperature point",
          "single temperature phase",
          "standard temperature and pressure",
          "standard time and pressure"
        ],
        "a": 2,
        "e": "0 °C and 101 325 Pa.",
        "h": "Standard conditions.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Practical Chemistry Techniques",
    "topicCode": "SS3-CHM-07",
    "module": "Practical Chemistry",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">Practical chemistry trains students to handle apparatus safely, make accurate measurements and identify substances by their observed properties.</div><h3 class=\"learn-subheading\">1. Titration</h3><ul style=\"margin-left:20px;\"><li>Apparatus: burette (delivers variable volumes), pipette (measures a fixed volume accurately), conical flask, white tile.</li><li>Rinse the burette with the solution it will hold, and the pipette likewise; the conical flask is rinsed only with distilled water.</li><li>Read the burette at eye level at the bottom of the meniscus; repeat until titres agree within 0.10 cm³.</li><li>End point is the point at which the indicator just changes colour.</li></ul><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Indicator</th><th>In acid</th><th>In alkali</th></tr></thead><tbody><tr><td>Methyl orange</td><td>Red</td><td>Yellow (orange at end point)</td></tr><tr><td>Phenolphthalein</td><td>Colourless</td><td>Pink</td></tr><tr><td>Litmus</td><td>Red</td><td>Blue</td></tr></tbody></table></div><p class=\"learn-p\">Methyl orange is used for strong acid–weak base and strong acid–strong base titrations; phenolphthalein suits weak acid–strong base and strong–strong titrations.</p><h3 class=\"learn-subheading\">2. Qualitative Analysis — Cations</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Cation</th><th>With NaOH(aq)</th><th>With NH₃(aq)</th></tr></thead><tbody><tr><td>Cu²⁺</td><td>Light blue precipitate, insoluble in excess</td><td>Light blue precipitate, dissolves in excess to give deep blue solution</td></tr><tr><td>Fe²⁺</td><td>Dirty green precipitate, insoluble in excess</td><td>Dirty green precipitate, insoluble in excess</td></tr><tr><td>Fe³⁺</td><td>Reddish-brown precipitate, insoluble in excess</td><td>Reddish-brown precipitate, insoluble in excess</td></tr><tr><td>Zn²⁺</td><td>White precipitate, soluble in excess</td><td>White precipitate, soluble in excess</td></tr><tr><td>Al³⁺</td><td>White precipitate, soluble in excess</td><td>White precipitate, insoluble in excess</td></tr><tr><td>Pb²⁺</td><td>White precipitate, soluble in excess</td><td>White precipitate, insoluble in excess</td></tr><tr><td>Ca²⁺</td><td>White precipitate, insoluble in excess</td><td>No precipitate or very slight</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Qualitative Analysis — Anions</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Anion</th><th>Test</th><th>Observation</th></tr></thead><tbody><tr><td>Cl⁻</td><td>AgNO₃ then dilute HNO₃</td><td>White precipitate soluble in aqueous ammonia</td></tr><tr><td>SO₄²⁻</td><td>BaCl₂ or Ba(NO₃)₂ then dilute HCl</td><td>White precipitate insoluble in acid</td></tr><tr><td>CO₃²⁻</td><td>Add dilute acid</td><td>Effervescence; gas turns limewater milky</td></tr><tr><td>NO₃⁻</td><td>Brown-ring test (FeSO₄ and concentrated H₂SO₄)</td><td>Brown ring at the junction</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Preparation of Gases</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Gas</th><th>Reagents</th><th>Collection</th></tr></thead><tbody><tr><td>Hydrogen</td><td>Zinc + dilute HCl or H₂SO₄</td><td>Downward displacement of water</td></tr><tr><td>Oxygen</td><td>MnO₂ + hydrogen peroxide</td><td>Over water or upward delivery</td></tr><tr><td>Carbon dioxide</td><td>Marble (CaCO₃) + dilute HCl</td><td>Upward delivery (denser than air)</td></tr><tr><td>Ammonia</td><td>NH₄Cl + Ca(OH)₂, heated</td><td>Downward delivery (less dense), dried with CaO</td></tr><tr><td>Chlorine</td><td>MnO₂ + concentrated HCl, heated</td><td>Upward delivery, after washing and drying</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "A pipette is used to:",
        "o": [
          "weigh solids",
          "heat solutions",
          "measure a fixed volume of solution accurately",
          "deliver variable volumes"
        ],
        "a": 2,
        "e": "It delivers a fixed volume, e.g. 25.0 cm³.",
        "h": "Fixed volume.",
        "yr": "SS3"
      },
      {
        "q": "A burette is used to:",
        "o": [
          "heat solutions",
          "measure a fixed volume only",
          "hold solids",
          "deliver variable volumes of a solution"
        ],
        "a": 3,
        "e": "The titrant is added from the burette.",
        "h": "Graduated tube with tap.",
        "yr": "SS3"
      },
      {
        "q": "Before use, the conical flask in a titration is rinsed with:",
        "o": [
          "distilled water only",
          "the indicator",
          "the acid",
          "the alkali"
        ],
        "a": 0,
        "e": "Rinsing with reagents would change the moles present.",
        "h": "Do not rinse with the solution.",
        "yr": "SS3"
      },
      {
        "q": "The burette should be read:",
        "o": [
          "from above",
          "from below the tap",
          "at eye level at the bottom of the meniscus",
          "at the top of the meniscus"
        ],
        "a": 2,
        "e": "This avoids parallax error.",
        "h": "Meniscus.",
        "yr": "SS3"
      },
      {
        "q": "Titre values are considered concordant when they agree within:",
        "o": [
          "0.10 cm³",
          "1.0 cm³",
          "0.50 cm³",
          "5.0 cm³"
        ],
        "a": 0,
        "e": "Repeat until consistent.",
        "h": "Close agreement.",
        "yr": "SS3"
      },
      {
        "q": "Methyl orange in an alkaline solution is:",
        "o": [
          "red",
          "pink",
          "yellow",
          "colourless"
        ],
        "a": 2,
        "e": "It is red in acid and yellow in alkali.",
        "h": "Colour.",
        "yr": "SS3"
      },
      {
        "q": "Phenolphthalein in alkaline solution is:",
        "o": [
          "red",
          "pink",
          "yellow",
          "colourless"
        ],
        "a": 1,
        "e": "It is colourless in acid.",
        "h": "Pink.",
        "yr": "SS3"
      },
      {
        "q": "The end point of a titration is:",
        "o": [
          "when the indicator just changes colour",
          "when the acid is added first",
          "when the burette is empty",
          "when the flask is full"
        ],
        "a": 0,
        "e": "It shows the reaction is complete.",
        "h": "Colour change.",
        "yr": "SS3"
      },
      {
        "q": "Cu²⁺ with excess aqueous ammonia gives:",
        "o": [
          "a white precipitate",
          "a green precipitate",
          "no reaction",
          "a deep blue solution"
        ],
        "a": 3,
        "e": "The precipitate dissolves to form the tetraamminecopper(II) complex.",
        "h": "Deep blue.",
        "yr": "SS3"
      },
      {
        "q": "Which cation gives a reddish-brown precipitate with NaOH?",
        "o": [
          "Zn²⁺",
          "Cu²⁺",
          "Fe³⁺",
          "Fe²⁺"
        ],
        "a": 2,
        "e": "Fe(OH)₃ is reddish-brown.",
        "h": "Rust colour.",
        "yr": "SS3"
      },
      {
        "q": "Which cation gives a dirty green precipitate with NaOH?",
        "o": [
          "Fe²⁺",
          "Fe³⁺",
          "Al³⁺",
          "Ca²⁺"
        ],
        "a": 0,
        "e": "Fe(OH)₂ is dirty green.",
        "h": "Iron(II).",
        "yr": "SS3"
      },
      {
        "q": "A white precipitate that dissolves in excess NaOH and in excess NH₃ indicates:",
        "o": [
          "Pb²⁺",
          "Al³⁺",
          "Zn²⁺",
          "Ca²⁺"
        ],
        "a": 2,
        "e": "Zinc hydroxide forms a soluble complex with both.",
        "h": "Zinc.",
        "yr": "SS3"
      },
      {
        "q": "A white precipitate soluble in excess NaOH but insoluble in excess NH₃ indicates:",
        "o": [
          "Al³⁺ or Pb²⁺",
          "Cu²⁺",
          "Ca²⁺ only",
          "Zn²⁺ only"
        ],
        "a": 0,
        "e": "Aluminium and lead hydroxides are amphoteric but do not dissolve in ammonia.",
        "h": "Not zinc.",
        "yr": "SS3"
      },
      {
        "q": "Ca²⁺ with NaOH gives a white precipitate that is:",
        "o": [
          "insoluble in excess",
          "soluble in excess",
          "blue",
          "green"
        ],
        "a": 0,
        "e": "Calcium hydroxide is not amphoteric.",
        "h": "Insoluble.",
        "yr": "SS3"
      },
      {
        "q": "The test for chloride ions uses:",
        "o": [
          "litmus",
          "silver nitrate solution",
          "limewater",
          "barium chloride"
        ],
        "a": 1,
        "e": "A white precipitate of AgCl forms, soluble in ammonia.",
        "h": "AgNO₃.",
        "yr": "SS3"
      },
      {
        "q": "Sulphate ions are detected with:",
        "o": [
          "silver nitrate",
          "litmus",
          "iodine",
          "barium chloride and dilute HCl"
        ],
        "a": 3,
        "e": "A white BaSO₄ precipitate insoluble in acid.",
        "h": "Barium.",
        "yr": "SS3"
      },
      {
        "q": "The gas released when a carbonate reacts with dilute acid is:",
        "o": [
          "hydrogen",
          "oxygen",
          "ammonia",
          "carbon dioxide"
        ],
        "a": 3,
        "e": "It turns limewater milky.",
        "h": "Effervescence.",
        "yr": "SS3"
      },
      {
        "q": "Hydrogen can be prepared using:",
        "o": [
          "NH₄Cl and Ca(OH)₂",
          "MnO₂ and H₂O₂",
          "marble and acid",
          "zinc and dilute hydrochloric acid"
        ],
        "a": 3,
        "e": "Zn + 2HCl → ZnCl₂ + H₂.",
        "h": "Metal + acid.",
        "yr": "SS3"
      },
      {
        "q": "Carbon dioxide is collected by:",
        "o": [
          "upward displacement of air",
          "upward delivery (downward displacement of air)",
          "in a syringe only",
          "over mercury only"
        ],
        "a": 1,
        "e": "CO₂ is denser than air.",
        "h": "Heavier than air.",
        "yr": "SS3"
      },
      {
        "q": "Ammonia is prepared by heating:",
        "o": [
          "ammonium chloride with calcium hydroxide",
          "zinc with acid",
          "MnO₂ with H₂O₂",
          "marble with acid"
        ],
        "a": 0,
        "e": "It is collected by downward delivery and dried with quicklime.",
        "h": "Ammonium salt + alkali.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Summary of Major Topics",
    "topicCode": "SS3-CHM-08",
    "module": "Consolidation of Core Concepts",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">This topic brings together essential ideas from SS1 to SS3, so that concepts in physical, inorganic and organic chemistry can be seen as one connected subject.</div><h3 class=\"learn-subheading\">1. Atomic Structure and the Periodic Table</h3><ul style=\"margin-left:20px;\"><li>Atomic number = number of protons; mass number = protons + neutrons; isotopes have the same atomic number but different mass numbers.</li><li>Across a period: atomic radius decreases, ionisation energy and electronegativity increase, metallic character decreases.</li><li>Down a group: atomic radius increases, ionisation energy decreases, reactivity of metals increases (Group 1) and of non-metals decreases (Group 17).</li></ul><h3 class=\"learn-subheading\">2. Chemical Bonding</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Bond</th><th>Formed by</th><th>Properties</th></tr></thead><tbody><tr><td>Ionic</td><td>Transfer of electrons (metal + non-metal)</td><td>High melting point; conducts when molten or dissolved</td></tr><tr><td>Covalent</td><td>Sharing of electrons (non-metals)</td><td>Low melting point; does not conduct</td></tr><tr><td>Metallic</td><td>Positive ions in a sea of delocalised electrons</td><td>Good conductors; malleable and ductile</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Acids, Bases, Salts and Redox</h3><ul style=\"margin-left:20px;\"><li>Acid + base → salt + water (neutralisation).</li><li>Oxidation is loss of electrons (or gain of oxygen); reduction is gain of electrons. Remember OIL RIG.</li><li>Oxidation number rules identify oxidising and reducing agents.</li></ul><h3 class=\"learn-subheading\">4. Organic Chemistry</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Homologous series</th><th>General formula</th><th>Example</th></tr></thead><tbody><tr><td>Alkanes</td><td>C<sub>n</sub>H<sub>2n+2</sub></td><td>Methane, ethane</td></tr><tr><td>Alkenes</td><td>C<sub>n</sub>H<sub>2n</sub></td><td>Ethene</td></tr><tr><td>Alkynes</td><td>C<sub>n</sub>H<sub>2n−2</sub></td><td>Ethyne</td></tr><tr><td>Alkanols</td><td>C<sub>n</sub>H<sub>2n+1</sub>OH</td><td>Ethanol</td></tr><tr><td>Alkanoic acids</td><td>C<sub>n</sub>H<sub>2n+1</sub>COOH</td><td>Ethanoic acid</td></tr></tbody></table></div><h3 class=\"learn-subheading\">5. Rates, Equilibrium and Energetics</h3><ul style=\"margin-left:20px;\"><li>Rate increases with concentration, temperature, surface area and catalyst.</li><li>Le Chatelier's principle: a system at equilibrium opposes any change imposed on it.</li><li>Exothermic reactions release heat (ΔH negative); endothermic reactions absorb heat (ΔH positive).</li></ul><h3 class=\"learn-subheading\">6. Electrolysis</h3><p class=\"learn-p\">Oxidation occurs at the anode and reduction at the cathode. Faraday's laws relate the mass deposited to the quantity of electricity, Q = It.</p>",
    "questions": [
      {
        "q": "The atomic number of an element is the number of:",
        "o": [
          "electron shells",
          "protons",
          "nucleons",
          "neutrons"
        ],
        "a": 1,
        "e": "It identifies the element.",
        "h": "In the nucleus.",
        "yr": "SS3"
      },
      {
        "q": "Isotopes of an element have the same:",
        "o": [
          "mass number",
          "number of protons",
          "number of neutrons",
          "relative atomic mass"
        ],
        "a": 1,
        "e": "They differ in neutron number.",
        "h": "Same atomic number.",
        "yr": "SS3"
      },
      {
        "q": "Across a period, ionisation energy generally:",
        "o": [
          "stays the same",
          "decreases",
          "increases",
          "becomes zero"
        ],
        "a": 2,
        "e": "Nuclear charge increases while shielding stays similar.",
        "h": "Left to right.",
        "yr": "SS3"
      },
      {
        "q": "Down a group, the atomic radius:",
        "o": [
          "increases",
          "decreases",
          "becomes zero",
          "stays constant"
        ],
        "a": 0,
        "e": "More shells are added.",
        "h": "More electron shells.",
        "yr": "SS3"
      },
      {
        "q": "The most reactive metals are found in:",
        "o": [
          "Group 1",
          "Group 18",
          "Group 14",
          "Group 17"
        ],
        "a": 0,
        "e": "Group 1 reactivity increases down the group.",
        "h": "Alkali metals.",
        "yr": "SS3"
      },
      {
        "q": "Ionic bonds are formed by:",
        "o": [
          "sharing of electrons",
          "delocalised electrons",
          "hydrogen only",
          "transfer of electrons"
        ],
        "a": 3,
        "e": "Between metals and non-metals.",
        "h": "Give and take.",
        "yr": "SS3"
      },
      {
        "q": "A compound that conducts electricity when molten but not when solid is probably:",
        "o": [
          "covalent",
          "a noble gas",
          "ionic",
          "metallic"
        ],
        "a": 2,
        "e": "Ions become mobile when molten.",
        "h": "Mobile ions.",
        "yr": "SS3"
      },
      {
        "q": "A covalent bond is formed by:",
        "o": [
          "attraction between cations and delocalised electrons",
          "sharing of electrons",
          "transfer of electrons",
          "nuclear forces"
        ],
        "a": 1,
        "e": "Between non-metal atoms.",
        "h": "Sharing.",
        "yr": "SS3"
      },
      {
        "q": "The general formula of alkanes is:",
        "o": [
          "CnH2n+2",
          "CnH2n+1OH",
          "CnH2n",
          "CnH2n−2"
        ],
        "a": 0,
        "e": "Methane is CH₄.",
        "h": "Saturated.",
        "yr": "SS3"
      },
      {
        "q": "The general formula of alkenes is:",
        "o": [
          "CnH2n+2",
          "CnH2nO",
          "CnH2n−2",
          "CnH2n"
        ],
        "a": 3,
        "e": "Ethene is C₂H₄.",
        "h": "One double bond.",
        "yr": "SS3"
      },
      {
        "q": "Ethyne belongs to the:",
        "o": [
          "alkanols",
          "alkanes",
          "alkynes",
          "alkenes"
        ],
        "a": 2,
        "e": "General formula CnH2n−2.",
        "h": "Triple bond.",
        "yr": "SS3"
      },
      {
        "q": "Oxidation is defined as:",
        "o": [
          "gain of electrons",
          "loss of oxygen",
          "loss of electrons",
          "gain of hydrogen"
        ],
        "a": 2,
        "e": "OIL RIG.",
        "h": "Oxidation Is Loss.",
        "yr": "SS3"
      },
      {
        "q": "Reduction takes place at the:",
        "o": [
          "anode",
          "cathode",
          "salt bridge",
          "electrolyte only"
        ],
        "a": 1,
        "e": "Reduction is gain of electrons at the cathode.",
        "h": "Negative electrode in electrolysis.",
        "yr": "SS3"
      },
      {
        "q": "Neutralisation produces:",
        "o": [
          "acid and water",
          "salt only",
          "hydrogen and oxygen",
          "salt and water"
        ],
        "a": 3,
        "e": "Acid + base → salt + water.",
        "h": "Acid–base.",
        "yr": "SS3"
      },
      {
        "q": "The rate of a reaction is increased by:",
        "o": [
          "raising the temperature",
          "decreasing the concentration",
          "lowering the temperature",
          "removing the catalyst"
        ],
        "a": 0,
        "e": "More particles have enough energy to react.",
        "h": "Speed.",
        "yr": "SS3"
      },
      {
        "q": "According to Le Chatelier's principle, if a system at equilibrium is disturbed, it:",
        "o": [
          "stops reacting",
          "becomes a solid",
          "shifts to oppose the change",
          "explodes"
        ],
        "a": 2,
        "e": "The equilibrium position adjusts.",
        "h": "Opposes.",
        "yr": "SS3"
      },
      {
        "q": "An exothermic reaction has ΔH:",
        "o": [
          "infinite",
          "zero always",
          "positive",
          "negative"
        ],
        "a": 3,
        "e": "It releases heat.",
        "h": "Heat given out.",
        "yr": "SS3"
      },
      {
        "q": "Faraday's laws relate the mass deposited during electrolysis to:",
        "o": [
          "size of the beaker",
          "colour of electrolyte",
          "quantity of electricity",
          "room temperature"
        ],
        "a": 2,
        "e": "Q = It.",
        "h": "Charge.",
        "yr": "SS3"
      },
      {
        "q": "The relative formula mass of a compound is found by:",
        "o": [
          "counting electrons",
          "adding the atomic masses of all its atoms",
          "subtracting atomic masses",
          "multiplying atomic numbers"
        ],
        "a": 1,
        "e": "e.g. H₂O = 18.",
        "h": "Sum.",
        "yr": "SS3"
      },
      {
        "q": "Which particle is negatively charged?",
        "o": [
          "Electron",
          "Alpha particle",
          "Neutron",
          "Proton"
        ],
        "a": 0,
        "e": "Found outside the nucleus.",
        "h": "Orbiting particles.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Chemistry in Everyday Life",
    "topicCode": "SS3-CHM-09",
    "module": "Chemistry and Society",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">Soaps, cosmetics, plastics and textiles all come from chemistry. Understanding their composition helps us choose and use them wisely.</div><h3 class=\"learn-subheading\">1. Soaps and Detergents</h3><ul style=\"margin-left:20px;\"><li><strong>Soap:</strong> sodium or potassium salt of a long-chain fatty acid, made by saponification of fats or oils with NaOH or KOH.</li><li>Soap has a hydrophobic (grease-loving) tail and a hydrophilic (water-loving) head, which emulsifies grease.</li><li>In <strong>hard water</strong>, Ca²⁺ and Mg²⁺ ions form insoluble scum with soap.</li><li><strong>Soapless detergents</strong> are made from petroleum chemicals; they lather in hard water, but non-biodegradable types cause pollution and foaming in rivers.</li></ul><h3 class=\"learn-subheading\">2. Cosmetics</h3><p class=\"learn-p\">Cosmetics include creams, lotions, lipsticks, perfumes, hair products and toothpaste. Common ingredients are oils, waxes, emulsifiers, water, pigments, fragrances and preservatives. Some skin-lightening products contain harmful chemicals such as mercury compounds or excessive hydroquinone, which can damage skin and organs.</p><h3 class=\"learn-subheading\">3. Plastics and Polymers</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Type</th><th>Behaviour</th><th>Examples</th></tr></thead><tbody><tr><td>Thermoplastics</td><td>Soften on heating and can be remoulded</td><td>Polythene, polystyrene, PVC</td></tr><tr><td>Thermosetting plastics</td><td>Harden permanently when heated</td><td>Bakelite, melamine</td></tr></tbody></table></div><p class=\"learn-p\">Plastics are formed by polymerisation; polythene is made from ethene. Most plastics are non-biodegradable, so waste plastic should be recycled or reused.</p><h3 class=\"learn-subheading\">4. Textiles</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Fibre</th><th>Type</th><th>Source or composition</th></tr></thead><tbody><tr><td>Cotton</td><td>Natural</td><td>Cellulose from the cotton plant</td></tr><tr><td>Wool</td><td>Natural</td><td>Protein from sheep</td></tr><tr><td>Silk</td><td>Natural</td><td>Protein from silkworms</td></tr><tr><td>Nylon</td><td>Synthetic</td><td>Polyamide made from petroleum chemicals</td></tr><tr><td>Polyester (Terylene)</td><td>Synthetic</td><td>Made from petrochemicals</td></tr></tbody></table></div><h3 class=\"learn-subheading\">5. Food and Drugs</h3><p class=\"learn-p\">Food additives (preservatives, colourings, flavourings) should be used within safe limits, and drugs should be used only as prescribed.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Soap forms scum with hard water, while detergents do not.</div>",
    "questions": [
      {
        "q": "Soap is the sodium or potassium salt of a:",
        "o": [
          "amino acid",
          "mineral acid",
          "short-chain alcohol",
          "long-chain fatty acid"
        ],
        "a": 3,
        "e": "It is made by saponification.",
        "h": "Fatty.",
        "yr": "SS3"
      },
      {
        "q": "The hydrophilic head of a soap molecule:",
        "o": [
          "dissolves in water",
          "repels water",
          "dissolves in grease",
          "is a hydrocarbon chain"
        ],
        "a": 0,
        "e": "The carboxylate end is water-loving.",
        "h": "Water-loving.",
        "yr": "SS3"
      },
      {
        "q": "The hydrophobic tail of soap dissolves in:",
        "o": [
          "salt",
          "water",
          "grease",
          "acid"
        ],
        "a": 2,
        "e": "It is a hydrocarbon chain.",
        "h": "Grease-loving.",
        "yr": "SS3"
      },
      {
        "q": "Hard water contains dissolved:",
        "o": [
          "potassium salts only",
          "sodium chloride only",
          "calcium and magnesium salts",
          "oxygen"
        ],
        "a": 2,
        "e": "Ca²⁺ and Mg²⁺ ions.",
        "h": "Scum.",
        "yr": "SS3"
      },
      {
        "q": "Soap forms scum with hard water because:",
        "o": [
          "it dissolves completely",
          "it forms insoluble calcium and magnesium salts",
          "it forms gas",
          "it becomes acidic"
        ],
        "a": 1,
        "e": "The scum is calcium or magnesium stearate.",
        "h": "Insoluble.",
        "yr": "SS3"
      },
      {
        "q": "Soapless detergents differ from soap because they:",
        "o": [
          "lather in hard water",
          "are made from fats",
          "form scum in hard water",
          "are always biodegradable"
        ],
        "a": 0,
        "e": "Their calcium salts are soluble.",
        "h": "No scum.",
        "yr": "SS3"
      },
      {
        "q": "A problem with non-biodegradable detergents is:",
        "o": [
          "scum in soft water only",
          "foaming and pollution of rivers",
          "production of oxygen",
          "increased soil fertility"
        ],
        "a": 1,
        "e": "Microorganisms cannot break them down.",
        "h": "Environment.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a thermoplastic?",
        "o": [
          "Polythene",
          "Melamine",
          "Vulcanised rubber",
          "Bakelite"
        ],
        "a": 0,
        "e": "It softens on heating.",
        "h": "Re-mouldable.",
        "yr": "SS3"
      },
      {
        "q": "Bakelite is an example of a:",
        "o": [
          "thermosetting plastic",
          "metal",
          "thermoplastic",
          "natural fibre"
        ],
        "a": 0,
        "e": "It cannot be remoulded after setting.",
        "h": "Hardens permanently.",
        "yr": "SS3"
      },
      {
        "q": "Polythene is made from:",
        "o": [
          "ethane",
          "ethanol",
          "ethene",
          "methane"
        ],
        "a": 2,
        "e": "By polymerisation.",
        "h": "Alkene.",
        "yr": "SS3"
      },
      {
        "q": "Polymerisation is the:",
        "o": [
          "neutralisation of acids",
          "joining of many small molecules (monomers) to form a large one",
          "dissolving of salts",
          "breaking of large molecules"
        ],
        "a": 1,
        "e": "Monomers form polymers.",
        "h": "Many units.",
        "yr": "SS3"
      },
      {
        "q": "Plastic waste is a problem because it is:",
        "o": [
          "biodegradable",
          "soluble in water",
          "non-biodegradable",
          "edible"
        ],
        "a": 2,
        "e": "It persists in the environment.",
        "h": "Cannot rot.",
        "yr": "SS3"
      },
      {
        "q": "Cotton is a:",
        "o": [
          "natural fibre",
          "polyester",
          "polyamide",
          "synthetic fibre"
        ],
        "a": 0,
        "e": "It is cellulose.",
        "h": "Plant.",
        "yr": "SS3"
      },
      {
        "q": "Nylon is a:",
        "o": [
          "cellulose",
          "polyamide",
          "protein",
          "polyester"
        ],
        "a": 1,
        "e": "Synthetic fibre from petrochemicals.",
        "h": "Amide links.",
        "yr": "SS3"
      },
      {
        "q": "Terylene is another name for:",
        "o": [
          "nylon",
          "polyester",
          "cotton",
          "silk"
        ],
        "a": 1,
        "e": "A synthetic fibre.",
        "h": "Resists creases.",
        "yr": "SS3"
      },
      {
        "q": "Skin-lightening creams containing mercury compounds are:",
        "o": [
          "safe for all",
          "edible",
          "used as fertiliser",
          "harmful to health"
        ],
        "a": 3,
        "e": "They can damage skin and organs.",
        "h": "Toxic.",
        "yr": "SS3"
      },
      {
        "q": "Cosmetics such as creams often contain oils, water and:",
        "o": [
          "cement",
          "bleach only",
          "sulphuric acid",
          "emulsifiers"
        ],
        "a": 3,
        "e": "Emulsifiers mix oil and water.",
        "h": "Blends.",
        "yr": "SS3"
      },
      {
        "q": "Detergents are usually manufactured from:",
        "o": [
          "petrochemicals",
          "clay",
          "sand",
          "fats only"
        ],
        "a": 0,
        "e": "They are soapless detergents.",
        "h": "Petroleum.",
        "yr": "SS3"
      },
      {
        "q": "Silk and wool are made of:",
        "o": [
          "protein",
          "starch",
          "cellulose",
          "polyester"
        ],
        "a": 0,
        "e": "Natural animal fibres.",
        "h": "Amino acids.",
        "yr": "SS3"
      },
      {
        "q": "The reaction of a fat with NaOH to make soap is:",
        "o": [
          "fermentation",
          "polymerisation",
          "saponification",
          "cracking"
        ],
        "a": 2,
        "e": "Alkaline hydrolysis.",
        "h": "Soap-making.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Practical Chemistry Review",
    "topicCode": "SS3-CHM-10",
    "module": "Practical Chemistry",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">This topic reviews the observations that identify indicators, gases and salts in practical examinations.</div><h3 class=\"learn-subheading\">1. Use of Indicators</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Indicator</th><th>Acid</th><th>Neutral</th><th>Alkali</th></tr></thead><tbody><tr><td>Litmus</td><td>Red</td><td>Purple</td><td>Blue</td></tr><tr><td>Methyl orange</td><td>Red</td><td>Orange</td><td>Yellow</td></tr><tr><td>Phenolphthalein</td><td>Colourless</td><td>Colourless</td><td>Pink</td></tr><tr><td>Universal indicator</td><td>Red/orange (pH 0–6)</td><td>Green (pH 7)</td><td>Blue/purple (pH 8–14)</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Identification of Gases</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Gas</th><th>Test and result</th></tr></thead><tbody><tr><td>Hydrogen</td><td>Burns with a 'pop' with a lighted splint</td></tr><tr><td>Oxygen</td><td>Relights a glowing splint</td></tr><tr><td>Carbon dioxide</td><td>Turns limewater milky</td></tr><tr><td>Ammonia</td><td>Turns damp red litmus blue; forms dense white fumes with hydrogen chloride</td></tr><tr><td>Chlorine</td><td>Bleaches damp litmus paper; greenish-yellow gas</td></tr><tr><td>Sulphur(IV) oxide</td><td>Turns acidified potassium dichromate(VI) from orange to green</td></tr><tr><td>Nitrogen(IV) oxide</td><td>Reddish-brown gas</td></tr><tr><td>Hydrogen chloride</td><td>Dense white fumes with ammonia</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Flame Tests</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Metal ion</th><th>Flame colour</th></tr></thead><tbody><tr><td>Na⁺</td><td>Persistent golden-yellow</td></tr><tr><td>K⁺</td><td>Lilac</td></tr><tr><td>Ca²⁺</td><td>Brick-red</td></tr><tr><td>Cu²⁺</td><td>Blue-green</td></tr><tr><td>Ba²⁺</td><td>Apple-green</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Effect of Heat on Salts</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Salt</th><th>Observation on heating</th></tr></thead><tbody><tr><td>Hydrated copper(II) sulphate</td><td>Blue crystals turn white; water condenses</td></tr><tr><td>Zinc oxide</td><td>Yellow when hot, white when cold</td></tr><tr><td>Lead(II) oxide</td><td>Reddish-brown when hot, yellow when cold</td></tr><tr><td>Copper(II) carbonate</td><td>Green powder turns black (CuO); CO₂ evolved</td></tr><tr><td>Nitrates of Na and K</td><td>Give nitrite and oxygen</td></tr><tr><td>Nitrates of most other metals</td><td>Give metal oxide, brown NO₂ and oxygen</td></tr></tbody></table></div><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Always state colour changes, gas evolved and the test used.</div>",
    "questions": [
      {
        "q": "Red litmus turns blue in:",
        "o": [
          "an acidic solution",
          "an alkaline solution",
          "any salt",
          "a neutral solution"
        ],
        "a": 1,
        "e": "Litmus is red in acid and blue in alkali.",
        "h": "Base.",
        "yr": "SS3"
      },
      {
        "q": "Methyl orange is red in:",
        "o": [
          "acid",
          "alkali",
          "water only",
          "neutral solution only"
        ],
        "a": 0,
        "e": "Yellow in alkali.",
        "h": "Acidic.",
        "yr": "SS3"
      },
      {
        "q": "Universal indicator is green at pH:",
        "o": [
          "4",
          "14",
          "7",
          "1"
        ],
        "a": 2,
        "e": "Neutral.",
        "h": "Neutral.",
        "yr": "SS3"
      },
      {
        "q": "Phenolphthalein is colourless in:",
        "o": [
          "both alkali and acid",
          "none of these",
          "acid",
          "alkali"
        ],
        "a": 2,
        "e": "It turns pink in alkali.",
        "h": "Acid.",
        "yr": "SS3"
      },
      {
        "q": "Hydrogen gas is identified by:",
        "o": [
          "turning limewater milky",
          "relighting a glowing splint",
          "a 'pop' sound with a lighted splint",
          "bleaching litmus"
        ],
        "a": 2,
        "e": "It burns with a squeaky pop.",
        "h": "Explosive.",
        "yr": "SS3"
      },
      {
        "q": "Oxygen is identified by:",
        "o": [
          "a pop with a lighted splint",
          "turning limewater milky",
          "relighting a glowing splint",
          "white fumes with HCl"
        ],
        "a": 2,
        "e": "It supports combustion.",
        "h": "Glowing splint.",
        "yr": "SS3"
      },
      {
        "q": "Carbon dioxide is tested with:",
        "o": [
          "litmus",
          "acidified potassium dichromate",
          "a glowing splint",
          "limewater"
        ],
        "a": 3,
        "e": "The limewater turns milky.",
        "h": "Calcium hydroxide solution.",
        "yr": "SS3"
      },
      {
        "q": "Ammonia turns damp red litmus paper:",
        "o": [
          "red",
          "colourless",
          "blue",
          "white"
        ],
        "a": 2,
        "e": "It is the only common alkaline gas.",
        "h": "Alkaline gas.",
        "yr": "SS3"
      },
      {
        "q": "Ammonia forms dense white fumes with:",
        "o": [
          "carbon dioxide",
          "hydrogen chloride",
          "hydrogen",
          "oxygen"
        ],
        "a": 1,
        "e": "NH₃ + HCl → NH₄Cl.",
        "h": "Ammonium chloride.",
        "yr": "SS3"
      },
      {
        "q": "Chlorine gas bleaches:",
        "o": [
          "damp litmus paper",
          "a glowing splint",
          "limewater",
          "dry sand"
        ],
        "a": 0,
        "e": "It is greenish-yellow.",
        "h": "Bleaching agent.",
        "yr": "SS3"
      },
      {
        "q": "Sulphur(IV) oxide turns acidified potassium dichromate(VI) from orange to:",
        "o": [
          "green",
          "colourless",
          "blue",
          "brown"
        ],
        "a": 0,
        "e": "It is reduced to chromium(III).",
        "h": "Colour change.",
        "yr": "SS3"
      },
      {
        "q": "The flame colour of sodium ions is:",
        "o": [
          "lilac",
          "brick-red",
          "golden yellow",
          "apple-green"
        ],
        "a": 2,
        "e": "It is persistent.",
        "h": "Yellow.",
        "yr": "SS3"
      },
      {
        "q": "Potassium ions give a flame colour of:",
        "o": [
          "lilac",
          "golden yellow",
          "brick-red",
          "blue-green"
        ],
        "a": 0,
        "e": "Seen through cobalt glass.",
        "h": "Violet.",
        "yr": "SS3"
      },
      {
        "q": "Calcium ions give a flame colour of:",
        "o": [
          "lilac",
          "apple-green",
          "yellow",
          "brick-red"
        ],
        "a": 3,
        "e": "Characteristic of calcium.",
        "h": "Red.",
        "yr": "SS3"
      },
      {
        "q": "Hydrated copper(II) sulphate on heating turns:",
        "o": [
          "white",
          "green",
          "red",
          "black"
        ],
        "a": 0,
        "e": "It loses water of crystallisation.",
        "h": "From blue crystals.",
        "yr": "SS3"
      },
      {
        "q": "Zinc oxide is _____ when hot and white when cold.",
        "o": [
          "green",
          "black",
          "blue",
          "yellow"
        ],
        "a": 3,
        "e": "The colour change is reversible.",
        "h": "Hot colour.",
        "yr": "SS3"
      },
      {
        "q": "Lead(II) oxide is reddish-brown when hot and _____ when cold.",
        "o": [
          "white",
          "blue",
          "black",
          "yellow"
        ],
        "a": 3,
        "e": "Reversible colour change.",
        "h": "Cold colour.",
        "yr": "SS3"
      },
      {
        "q": "Heating copper(II) carbonate gives a:",
        "o": [
          "blue solid",
          "white solid and water",
          "black solid and carbon dioxide",
          "green solid and oxygen"
        ],
        "a": 2,
        "e": "CuCO₃ → CuO + CO₂.",
        "h": "Green to black.",
        "yr": "SS3"
      },
      {
        "q": "Heating nitrates of sodium and potassium gives:",
        "o": [
          "metal and oxygen",
          "metal hydroxide",
          "metal oxide and NO₂",
          "the nitrite and oxygen"
        ],
        "a": 3,
        "e": "Most other nitrates give oxide, NO₂ and O₂.",
        "h": "Nitrite.",
        "yr": "SS3"
      },
      {
        "q": "The gas that turns acidified potassium dichromate green is:",
        "o": [
          "carbon dioxide",
          "hydrogen",
          "oxygen",
          "sulphur(IV) oxide"
        ],
        "a": 3,
        "e": "SO₂ is a reducing agent.",
        "h": "Reducing gas.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Data Interpretation and Report Writing",
    "topicCode": "SS3-CHM-11",
    "module": "Practical Chemistry",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">Good practical work depends on recording, presenting and interpreting data correctly and reporting conclusions honestly.</div><h3 class=\"learn-subheading\">1. Recording Results</h3><ul style=\"margin-left:20px;\"><li>Use tables with clear headings and units (e.g. volume/cm³, time/s).</li><li>Record readings immediately and to the precision of the instrument (burette to 0.05 cm³, for example).</li><li>Repeat measurements to obtain consistent values and calculate the mean (average titre).</li></ul><h3 class=\"learn-subheading\">2. Drawing Graphs</h3><ul style=\"margin-left:20px;\"><li>Plot the independent variable on the x-axis and the dependent variable on the y-axis.</li><li>Choose a suitable scale that uses more than half of the graph paper.</li><li>Label axes with quantity and unit; mark points accurately; draw a smooth best-fit line or curve.</li><li>Gradient = change in y ÷ change in x, using a large triangle on the line.</li></ul><h3 class=\"learn-subheading\">3. Analysing Tables and Graphs</h3><p class=\"learn-p\">Identify trends (increasing, decreasing, constant), read off values by interpolation (inside the data) or extrapolation (beyond the data), and locate anomalous results that do not fit the pattern.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Example:</strong> A graph of volume of gas against time that levels off shows that the reaction has finished. A steeper gradient means a faster rate.</div><h3 class=\"learn-subheading\">4. Structure of a Practical Report</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Section</th><th>Content</th></tr></thead><tbody><tr><td>Title and aim</td><td>What is being investigated and why</td></tr><tr><td>Apparatus and materials</td><td>Equipment and chemicals used</td></tr><tr><td>Procedure</td><td>Steps in the order carried out, in the past tense</td></tr><tr><td>Results</td><td>Tables and graphs</td></tr><tr><td>Calculations</td><td>Working shown with units</td></tr><tr><td>Conclusion</td><td>Statement supported by the results</td></tr><tr><td>Precautions and sources of error</td><td>Safety steps and reasons for inaccuracy</td></tr></tbody></table></div><h3 class=\"learn-subheading\">5. Drawing Valid Conclusions</h3><p class=\"learn-p\">A conclusion must be based on the data collected and answer the aim. Do not claim more than the evidence supports; state possible sources of error, such as parallax error, incomplete reaction or impure reagents.</p>",
    "questions": [
      {
        "q": "In a table of results, column headings should include:",
        "o": [
          "no units",
          "only the colour",
          "only the name",
          "the quantity and its unit"
        ],
        "a": 3,
        "e": "Example: volume/cm³.",
        "h": "Clear labels.",
        "yr": "SS3"
      },
      {
        "q": "An average titre is obtained by:",
        "o": [
          "choosing the largest value",
          "multiplying the titres",
          "choosing the smallest value",
          "adding concordant titres and dividing by their number"
        ],
        "a": 3,
        "e": "Only consistent values are averaged.",
        "h": "Mean.",
        "yr": "SS3"
      },
      {
        "q": "On a graph, the independent variable is normally plotted on the:",
        "o": [
          "x-axis",
          "both axes",
          "y-axis",
          "neither axis"
        ],
        "a": 0,
        "e": "The dependent variable goes on the y-axis.",
        "h": "Horizontal.",
        "yr": "SS3"
      },
      {
        "q": "When drawing a graph, the scale should:",
        "o": [
          "use less than a quarter",
          "use more than half of the graph paper",
          "be tiny",
          "be random"
        ],
        "a": 1,
        "e": "It makes plotting accurate.",
        "h": "Use space.",
        "yr": "SS3"
      },
      {
        "q": "The line on a graph should be:",
        "o": [
          "a random line",
          "a horizontal line only",
          "a smooth best-fit line or curve",
          "joined dot to dot with a zigzag"
        ],
        "a": 2,
        "e": "It shows the trend.",
        "h": "Best fit.",
        "yr": "SS3"
      },
      {
        "q": "The gradient of a line is:",
        "o": [
          "change in x divided by change in y",
          "change in y divided by change in x",
          "the sum of x and y",
          "the area under it"
        ],
        "a": 1,
        "e": "Use a large triangle.",
        "h": "Rise over run.",
        "yr": "SS3"
      },
      {
        "q": "Reading a value between plotted points is called:",
        "o": [
          "integration",
          "extraction",
          "extrapolation",
          "interpolation"
        ],
        "a": 3,
        "e": "Inside the range of data.",
        "h": "Between.",
        "yr": "SS3"
      },
      {
        "q": "Extending a line beyond the plotted data to predict values is:",
        "o": [
          "extrapolation",
          "extraction",
          "interpolation",
          "oscillation"
        ],
        "a": 0,
        "e": "It is less reliable.",
        "h": "Beyond.",
        "yr": "SS3"
      },
      {
        "q": "A result that does not fit the pattern is called:",
        "o": [
          "a constant",
          "an anomalous result",
          "an average",
          "a variable"
        ],
        "a": 1,
        "e": "It may be due to error.",
        "h": "Outlier.",
        "yr": "SS3"
      },
      {
        "q": "A gas volume–time graph that levels off shows that the reaction has:",
        "o": [
          "doubled in rate",
          "finished",
          "stopped being chemical",
          "just started"
        ],
        "a": 1,
        "e": "No more gas is produced.",
        "h": "Plateau.",
        "yr": "SS3"
      },
      {
        "q": "A steeper gradient on a volume–time graph means the reaction rate is:",
        "o": [
          "faster",
          "zero",
          "slower",
          "constant only"
        ],
        "a": 0,
        "e": "More gas per unit time.",
        "h": "Speed.",
        "yr": "SS3"
      },
      {
        "q": "The 'aim' of a practical report states:",
        "o": [
          "the apparatus prices",
          "the errors only",
          "what is being investigated",
          "the results only"
        ],
        "a": 2,
        "e": "It begins the report.",
        "h": "Purpose.",
        "yr": "SS3"
      },
      {
        "q": "The procedure of a report is usually written in the:",
        "o": [
          "future tense",
          "present continuous tense",
          "imperative only",
          "past tense"
        ],
        "a": 3,
        "e": "It describes what was done.",
        "h": "Already done.",
        "yr": "SS3"
      },
      {
        "q": "Which section of a report shows working with units?",
        "o": [
          "Calculations",
          "Apparatus",
          "Aim",
          "Title"
        ],
        "a": 0,
        "e": "All steps are shown.",
        "h": "Maths.",
        "yr": "SS3"
      },
      {
        "q": "A conclusion should:",
        "o": [
          "exaggerate the findings",
          "be unrelated to the aim",
          "be supported by the results and answer the aim",
          "ignore the data"
        ],
        "a": 2,
        "e": "It must follow from the data.",
        "h": "Evidence.",
        "yr": "SS3"
      },
      {
        "q": "Parallax error occurs when a reading is taken:",
        "o": [
          "from the wrong eye level",
          "at the correct meniscus",
          "with a clean apparatus",
          "after repeating"
        ],
        "a": 0,
        "e": "It is a source of error.",
        "h": "Angle.",
        "yr": "SS3"
      },
      {
        "q": "An example of a safety precaution is:",
        "o": [
          "tasting chemicals",
          "running in the laboratory",
          "working alone in a locked lab",
          "wearing safety goggles"
        ],
        "a": 3,
        "e": "Always protect the eyes.",
        "h": "Protect.",
        "yr": "SS3"
      },
      {
        "q": "Repeating measurements helps to:",
        "o": [
          "improve reliability",
          "increase errors",
          "avoid recording",
          "hide results"
        ],
        "a": 0,
        "e": "Consistent results are more trustworthy.",
        "h": "Repeat.",
        "yr": "SS3"
      },
      {
        "q": "Which is a possible source of error in titration?",
        "o": [
          "Using clean apparatus",
          "Overshooting the end point",
          "Reading at eye level",
          "Rinsing correctly"
        ],
        "a": 1,
        "e": "Too much titrant is added.",
        "h": "Colour change missed.",
        "yr": "SS3"
      },
      {
        "q": "A burette reading should be recorded to the nearest:",
        "o": [
          "0.05 cm³",
          "0.5 dm³",
          "1 dm³",
          "5 cm³"
        ],
        "a": 0,
        "e": "Instrument precision.",
        "h": "Small scale.",
        "yr": "SS3"
      }
    ]
  }
];
